import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Download,
  FileText,
  Eye,
  Shield,
  Users,
  Cog,
  Calendar,
  DollarSign,
  MessageSquare,
  Database,
  Smartphone
} from 'lucide-react';

const Manuales = () => {
  const { user, API } = useContext(AuthContext);
  const [selectedManual, setSelectedManual] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [manualContent, setManualContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [manualesReales, setManualesReales] = useState([]);
  const [loadingManuales, setLoadingManuales] = useState(true);

  // Cargar manuales disponibles del backend
  React.useEffect(() => {
    loadManuales();
  }, []);

  const loadManuales = async () => {
    try {
      setLoadingManuales(true);
      const response = await fetch(`${API}/manuales/list`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setManualesReales(data.manuales || []);
      }
    } catch (error) {
      console.error('Error loading manuals:', error);
      toast.error('Error al cargar los manuales');
    } finally {
      setLoadingManuales(false);
    }
  };

  // Lista de manuales disponibles (template estático como fallback)
  const manualesTemplate = [
    {
      id: 'manual-profesor-pdf-2026',
      titulo: '👨‍🏫 Manual del Profesor 2026 (PDF)',
      descripcion: 'Manual completo para profesores. Incluye: gestión de clases, subir materiales y videos, crear evaluaciones y tareas, calificar trabajos, control de asistencia QR, comunicaciones y notificaciones. ¡NUEVO 2026!',
      icon: BookOpen,
      categoria: 'Oficial',
      roles: ['admin', 'profesor'],
      archivo: 'MANUAL_PROFESOR_IBA_CONNECT.pdf',
      tamaño: '22 KB',
      version: '2026.1',
      color: 'bg-purple-600',
      disponible: true,
      destacado: true
    },
    {
      id: 'manual-estudiante-pdf-2026',
      titulo: '📘 Manual del Estudiante 2026 (PDF)',
      descripcion: 'Manual actualizado y completo para estudiantes. Incluye: Dashboard, Área Académica, Comunicaciones, Registraciones, Biblia, Denarios y FAQ. ¡NUEVO 2026!',
      icon: BookOpen,
      categoria: 'Oficial',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      archivo: 'MANUAL_ESTUDIANTE_IBA_CONNECT.pdf',
      tamaño: '28 KB',
      version: '2026.1',
      color: 'bg-blue-600',
      disponible: true,
      destacado: true
    },
    {
      id: 'manual-lms-word',
      titulo: 'Manual Completo LMS - Formato Word',
      descripcion: 'Manual completo del sistema en formato Microsoft Word (.docx) con todas las funcionalidades, áreas y submenús detallados.',
      icon: FileText,
      categoria: 'Oficial',
      roles: ['admin', 'profesor', 'estudiante'],
      archivo: 'MANUAL_COMPLETO_LMS_IBA.docx',
      tamaño: '40.7 KB',
      version: '2.0',
      color: 'bg-green-600',
      disponible: true
    },
    {
      id: 'manual-lms-word-extendido',
      titulo: 'Manual LMS Extendido - Formato Word',
      descripcion: 'Manual extendido (~22 páginas) con información técnica avanzada, guías paso a paso y soluciones detalladas en formato Word.',
      icon: FileText,
      categoria: 'Técnico',
      roles: ['admin', 'profesor', 'estudiante'],
      archivo: 'MANUAL_COMPLETO_LMS_IBA_EXTENDIDO.docx',
      tamaño: '45.2 KB',
      version: '2.0 Extendida',
      color: 'bg-purple-600',
      disponible: true
    },
    {
      id: 'funcionalidades-completas',
      titulo: 'Manual Completo de Funcionalidades',
      descripcion: 'Documento completo con todas las funciones del sistema LMS, roles de usuario y características técnicas.',
      icon: BookOpen,
      categoria: 'General',
      roles: ['admin', 'profesor', 'estudiante'],
      archivo: 'FUNCIONALIDADES_COMPLETAS_LMS.txt',
      tamaño: '25 KB',
      version: '2024.1',
      color: 'bg-blue-500',
      disponible: true
    },
    {
      id: 'guia-backups',
      titulo: 'Guía de Gestión de Backups',
      descripcion: 'Manual completo para administradores sobre el sistema de respaldos automáticos y restauración de datos.',
      icon: Database,
      categoria: 'Administración',
      roles: ['admin'],
      archivo: 'GUIA_BACKUP_DATOS.md',
      tamaño: '18 KB',
      version: '2024.1',
      color: 'bg-red-500',
      disponible: true
    },
    {
      id: 'manual-usuarios',
      titulo: 'Manual de Gestión de Usuarios',
      descripcion: 'Guía para administradores sobre creación, edición y gestión de usuarios del sistema.',
      icon: Users,
      categoria: 'Administración',
      roles: ['admin'],
      archivo: 'manual_usuarios.pdf',
      tamaño: 'Próximamente',
      version: 'En desarrollo',
      color: 'bg-green-500',
      disponible: false
    },
    {
      id: 'guia-responsive',
      titulo: 'Guía de Uso en Dispositivos Móviles',
      descripcion: 'Manual de uso optimizado para smartphones y tablets, con tips de navegación móvil.',
      icon: Smartphone,
      categoria: 'Usuario',
      roles: ['admin', 'profesor', 'estudiante'],
      archivo: 'guia_mobile.pdf',
      tamaño: 'Próximamente',
      version: 'En desarrollo',
      color: 'bg-purple-500',
      disponible: false
    },
    {
      id: 'manual-clases',
      titulo: 'Manual de Gestión de Clases',
      descripción: 'Guía completa para profesores sobre creación y gestión de clases, asignación de estudiantes y evaluaciones.',
      icon: Calendar,
      categoria: 'Profesores',
      roles: ['admin', 'profesor'],
      archivo: 'manual_clases.pdf',
      tamaño: 'Próximamente',
      version: 'En desarrollo',
      color: 'bg-indigo-500',
      disponible: false
    },
    {
      id: 'manual-pagos',
      titulo: 'Manual de Sistema de Pagos',
      descripción: 'Documentación del sistema financiero, métodos de pago y gestión de registraciones.',
      icon: DollarSign,
      categoria: 'Administración',
      roles: ['admin'],
      archivo: 'manual_pagos.pdf',
      tamaño: 'Próximamente',
      version: 'En desarrollo',
      color: 'bg-yellow-500',
      disponible: false
    }
  ];

  // Combinar manuales reales con template para mostrar vista completa
  const combinarManuales = () => {
    const manualesMap = new Map();
    
    // Agregar manuales reales primero
    manualesReales.forEach(manual => {
      manualesMap.set(manual.id, {
        ...manual,
        titulo: manual.title,
        descripcion: manual.description,
        categoria: manual.category,
        archivo: manual.filename,
        tamaño: `${Math.round(manual.size / 1024)} KB`,
        version: '2024.1',
        disponible: manual.available,
        icon: manual.id === 'funcionalidades-completas' ? BookOpen : Database,
        color: manual.category === 'General' ? 'bg-blue-500' : 'bg-red-500'
      });
    });
    
    // Agregar manuales template que no estén en los reales
    manualesTemplate.forEach(manual => {
      if (!manualesMap.has(manual.id)) {
        manualesMap.set(manual.id, manual);
      }
    });
    
    return Array.from(manualesMap.values()).filter(manual => 
      manual.roles.includes(user?.rol || 'estudiante')
    );
  };

  const manualesDisponibles = combinarManuales();

  const getCategoriaColor = (categoria) => {
    const colors = {
      'General': 'bg-blue-100 text-blue-800',
      'Administración': 'bg-red-100 text-red-800',
      'Profesores': 'bg-indigo-100 text-indigo-800',
      'Usuario': 'bg-green-100 text-green-800'
    };
    return colors[categoria] || 'bg-gray-100 text-gray-800';
  };

  const handleViewManual = async (manual) => {
    if (!manual.disponible || manual.archivo.includes('Próximamente') || manual.tamaño === 'Próximamente') {
      toast.info('Este manual estará disponible próximamente');
      return;
    }

    // Para archivos Word, no se puede mostrar vista previa - redirigir a descarga
    if (manual.archivo.endsWith('.docx')) {
      toast.info('Los archivos Word no pueden mostrarse en vista previa. Iniciando descarga automática...');
      await handleDownloadManual(manual);
      return;
    }

    // Para PDFs, abrir en nueva pestaña
    if (manual.archivo.endsWith('.pdf')) {
      try {
        const response = await fetch(`${API}/manuales/download/${manual.archivo}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          toast.success('Abriendo PDF en nueva pestaña');
        } else {
          toast.error('Error al abrir el PDF');
        }
      } catch (error) {
        console.error('Error viewing PDF:', error);
        toast.error('Error al abrir el PDF');
      }
      return;
    }

    setSelectedManual(manual);
    setLoading(true);
    setIsViewDialogOpen(true);

    try {
      const response = await fetch(`${API}/manuales/download/${manual.archivo}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Mostrar solo las primeras 50 líneas para preview
        const lines = data.content.split('\n').slice(0, 50);
        setManualContent(lines.join('\n') + '\n\n... [Contenido truncado - Descarga el archivo completo para ver todo]');
      } else {
        // Fallback al contenido estático si falla el backend
        const content = `
=============================================================================
                   INSTITUTO BÍBLICO APOSTÓLICO DEL URUGUAY
                        SISTEMA LMS - FUNCIONALIDADES COMPLETAS
=============================================================================

📚 DESCRIPCIÓN GENERAL
El LMS es una plataforma educativa completa para la gestión integral de 
estudiantes, profesores, clases, eventos y contenido educativo.

🎯 ROLES DE USUARIO
- ADMINISTRADOR: Control total del sistema
- PROFESOR: Gestión de clases y estudiantes asignados  
- ESTUDIANTE: Acceso a contenido educativo y participación en clases

=============================================================================
                           FUNCIONALIDADES PRINCIPALES
=============================================================================

🔐 1. SISTEMA DE AUTENTICACIÓN Y USUARIOS
- Login con email y contraseña
- Gestión completa de usuarios por administradores
- Perfiles personalizables con fotos
- Roles y permisos específicos

📚 2. GESTIÓN DE CLASES Y CONTENIDO ACADÉMICO  
- Creación y edición de clases
- Asignación a múltiples semestres
- Zona horaria de Montevideo configurada
- Enlaces Zoom y playlists YouTube
- Días de la semana configurables

🛡️ 3. SISTEMA DE RESPALDO Y SEGURIDAD
- Backups automáticos en cada reinicio
- Panel de gestión de respaldos
- Restauración inteligente de datos
- Protección total contra pérdida de información

📱 4. COMPATIBILIDAD RESPONSIVE
- Optimizado para móviles, tablets y desktop
- Formularios con scroll en dispositivos pequeños
- Interfaz completamente funcional en smartphones

Para ver el manual completo, descarga el archivo completo.
        `;
        setManualContent(content);
      }
    } catch (error) {
      console.error('Error loading manual:', error);
      toast.error('Error al cargar el manual');
      // Mostrar contenido de fallback
      setManualContent('Error al cargar el contenido. Por favor, intenta descargar el archivo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadManual = async (manual) => {
    if (!manual.disponible || manual.archivo.includes('Próximamente') || manual.tamaño === 'Próximamente') {
      toast.info('Este manual estará disponible próximamente');
      return;
    }

    try {
      // Para archivos Word (.docx) y PDF (.pdf), descargar desde el backend
      if (manual.archivo.endsWith('.docx') || manual.archivo.endsWith('.pdf')) {
        toast.info('Iniciando descarga del manual...');
        
        const response = await fetch(`${API}/manuales/download/${manual.archivo}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          
          // Verificar que realmente obtuvimos un archivo
          if (blob.size === 0) {
            toast.error('El archivo descargado está vacío');
            return;
          }
          
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = manual.archivo;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          
          // Cleanup
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);
          
          toast.success(`${manual.titulo} descargado exitosamente (${Math.round(blob.size / 1024)} KB)`);
        } else {
          const errorText = await response.text();
          console.error('Error response:', response.status, errorText);
          toast.error(`Error al descargar el manual Word: ${response.status}`);
        }
        return;
      }

      // Para otros archivos, usar la API backend
      const response = await fetch(`${API}/manuales/download/${manual.archivo}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Crear y descargar el archivo
        const blob = new Blob([data.content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = manual.archivo;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`${manual.titulo} descargado exitosamente`);
      } else {
        toast.error('Error al descargar el manual');
      }
    } catch (error) {
      console.error('Error downloading manual:', error);
      toast.error('Error al descargar el manual');
    }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
              className="text-gray-600 hover:text-gray-800"
              data-testid="back-button"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Volver
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <BookOpen className="h-8 w-8 text-blue-600" />
                Manuales y Documentación
              </h1>
              <p className="text-gray-600 mt-2">
                Accede a guías, manuales y documentación del sistema LMS
              </p>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-4 text-sm text-gray-500">
            <div className="text-center">
              <div className="font-bold text-lg text-blue-600">
                {manualesDisponibles.length}
              </div>
              <div>Manuales</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg text-green-600">
                {manualesDisponibles.filter(m => !m.archivo.includes('Próximamente')).length}
              </div>
              <div>Disponibles</div>
            </div>
          </div>
        </div>
      </div>

      {/* Manuales Grid */}
      {loadingManuales ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando manuales disponibles...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {manualesDisponibles.map((manual) => {
          const IconComponent = manual.icon;
          return (
            <Card key={manual.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-full ${manual.color} text-white flex-shrink-0`}>
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <Badge className={getCategoriaColor(manual.categoria)}>
                    {manual.categoria}
                  </Badge>
                </div>
                <CardTitle className="text-lg">{manual.titulo}</CardTitle>
              </CardHeader>
              
              <CardContent>
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {manual.descripcion}
                </p>
                
                <div className="space-y-2 text-xs text-gray-500 mb-4">
                  <div className="flex justify-between">
                    <span>Archivo:</span>
                    <span className="font-mono">{manual.archivo}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tamaño:</span>
                    <span>{manual.tamaño}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Versión:</span>
                    <span>{manual.version}</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewManual(manual)}
                    className="flex-1"
                    disabled={manual.tamaño === 'Próximamente' || manual.archivo.includes('Próximamente')}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Ver
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDownloadManual(manual)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={manual.tamaño === 'Próximamente' || manual.archivo.includes('Próximamente')}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Descargar
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        </div>
      )}

      {/* Info Cards */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="font-semibold text-blue-900">Manuales Actualizados</h3>
                <p className="text-sm text-blue-700">Documentación siempre al día</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-900">Acceso Seguro</h3>
                <p className="text-sm text-green-700">Solo usuarios autorizados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Smartphone className="h-8 w-8 text-purple-600" />
              <div>
                <h3 className="font-semibold text-purple-900">Mobile Friendly</h3>
                <p className="text-sm text-purple-700">Accesible desde cualquier dispositivo</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Manual Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl w-full mx-2 sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              {selectedManual?.titulo}
            </DialogTitle>
            <DialogDescription>
              Vista previa del manual - Descarga el archivo completo para mejor experiencia
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-1 max-h-[60vh]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Cargando manual...</p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border rounded-lg p-4">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {manualContent}
                </pre>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Cerrar
            </Button>
            <Button 
              onClick={() => handleDownloadManual(selectedManual)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar Manual Completo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Manuales;